/**
 * A choice kept in localStorage: a view, a panel's open state. A stored value
 * outside `allowed` (an old build, a hand edit) is ignored; a browser that
 * refuses storage keeps the choice for the page's lifetime only.
 */

import { useCallback, useState } from 'react';

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const stored = localStorage.getItem(key);
    return allowed.find((option) => option === stored) ?? fallback;
  } catch {
    // No storage at all (blocked, or no window): the fallback holds.
    return fallback;
  }
}

export function useStoredChoice<T extends string>(key: string, allowed: readonly T[], fallback: T): [T, (next: T) => void] {
  const [held, setHeld] = useState(() => ({ key, value: read(key, allowed, fallback) }));
  // A new key (another person signed in) reads its own stored value.
  let value = held.value;
  if (held.key !== key) {
    value = read(key, allowed, fallback);
    setHeld({ key, value });
  }

  const choose = useCallback(
    (next: T) => {
      setHeld({ key, value: next });
      try {
        localStorage.setItem(key, next);
      } catch {
        // Storage is a convenience; the choice still holds for this page.
      }
    },
    [key],
  );

  return [value, choose];
}
